import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribeBotChallenge, type PendingChallenge } from '../utils/bot-challenge-client';
import { recaptchaClient } from '../utils/recaptcha-client';
import { trackError, ErrorSeverity } from '../utils/error-tracking';

/**
 * The reCAPTCHA checkbox, shown when a login-code send or a chat message comes
 * back RECAPTCHA_CHALLENGE (utils/bot-challenge-client.ts). Mounted once in the
 * layout, above every other popup, since the send that asked for it is often
 * inside the login modal.
 */
export default function BotChallengeHost() {
	const [challenge, setChallenge] = useState<PendingChallenge | null>(null);
	// The challenge whose widget failed, so a new challenge starts clean.
	const [failedFor, setFailedFor] = useState<PendingChallenge | null>(null);
	const widgetRef = useRef<HTMLDivElement>(null);

	useEffect(() => subscribeBotChallenge(setChallenge), []);

	useEffect(() => {
		const container = widgetRef.current;
		const render = recaptchaClient()?.render;
		if (!challenge || !container || !render) return;
		// enterprise.js is already on the page for the score; a second key's
		// checkbox draws into its own fresh element each time.
		const target = document.createElement('div');
		container.replaceChildren(target);
		try {
			render(target, {
				sitekey: challenge.siteKey,
				// No expired-callback: on expiry the widget unticks itself and the
				// request keeps waiting for a fresh tick or Cancel.
				callback: (token) => challenge.resolve(token),
				'error-callback': () => setFailedFor(challenge),
			});
		} catch (error) {
			trackError(error as Error, ErrorSeverity.WARNING, { component: 'BotChallengeHost', action: 'render' });
			queueMicrotask(() => setFailedFor(challenge));
		}
	}, [challenge]);

	if (!challenge) return null;
	const failed = failedFor === challenge || typeof recaptchaClient()?.render !== 'function';

	// Straight under <body>: inside the layout it shares a stacking context that
	// sits below the OneTrust cookie banner, whatever its own z-index says, and on
	// a phone's first visit the banner covered the check, Cancel included.
	return createPortal(
		<>
			<div className="bot-challenge-backdrop" onClick={() => challenge.resolve(undefined)} />
			{/* The app's own popup colours (styles/components/mobile-info-popup.scss):
			    Tailwind's gray-800 is a blue-grey outside the dash. Keyed off <html>'s
			    theme class, since the portal sits outside the layout's .theme. */}
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="bot-challenge-title"
				data-testid="bot-challenge"
				className="bot-challenge"
			>
				<h3 id="bot-challenge-title" className="bot-challenge__title">
					Quick check
				</h3>
				<p className="bot-challenge__explain">Tick the box to show you are a person.</p>
				<div ref={widgetRef} data-testid="bot-challenge-widget" className="bot-challenge__widget" />
				{failed && (
					<p data-testid="bot-challenge-error" className="bot-challenge__error">
						The check could not load. Refresh the page and try again.
					</p>
				)}
				<div className="bot-challenge__actions">
					<button
						type="button"
						data-testid="bot-challenge-cancel"
						onClick={() => challenge.resolve(undefined)}
						className="hollowButton"
					>
						Cancel
					</button>
				</div>
			</div>
		</>,
		document.body
	);
}
